// ABOUTME: Connection testing step for the DataSourceWizard.
// ABOUTME: Shows loading state, success, or failure with retry option.

import { Button, LoadingSpinner } from '@/components/ui';
import type { TestConnectionResult } from '@/services/api/dataSourceClient';

interface TestingStepProps {
  isLoading: boolean;
  testResult: TestConnectionResult | null;
  onRetry: () => void;
  onNext: () => void;
  onBack: () => void;
  onCancel: () => void;
}

/**
 * Success icon for when connection test passes.
 */
function SuccessIcon() {
  return (
    <svg
      className="w-16 h-16 text-success"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

/**
 * Error icon for when connection test fails.
 */
function ErrorIcon() {
  return (
    <svg
      className="w-16 h-16 text-error"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

export function TestingStep({
  isLoading,
  testResult,
  onRetry,
  onNext,
  onBack,
  onCancel,
}: TestingStepProps) {
  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center text-center px-6 py-12">
        <LoadingSpinner size="lg" />
        <h2 className="text-xl font-semibold text-surface-900 mt-6 mb-2">
          Testing Connection
        </h2>
        <p className="text-surface-600">
          Verifying your credentials and discovering available catalogs...
        </p>
      </div>
    );
  }

  // Success state
  if (testResult?.success) {
    const catalogCount = testResult.catalogs?.length || 0;
    const schemaCount = testResult.schemas?.length || 0;

    return (
      <div className="flex flex-col items-center text-center px-6 py-8">
        <SuccessIcon />

        <h2 className="text-xl font-semibold text-surface-900 mt-4 mb-2">
          Connection Successful
        </h2>

        <p className="text-surface-600 mb-6">
          Your Databricks workspace is ready to use.
        </p>

        <div className="bg-surface-50 rounded-lg p-4 mb-8 w-full max-w-sm">
          <h3 className="text-sm font-medium text-surface-700 mb-3">
            Discovered Resources
          </h3>
          <div className="flex justify-around">
            <div className="text-center">
              <div className="text-2xl font-semibold text-primary-600">
                {catalogCount}
              </div>
              <div className="text-sm text-surface-500">
                {catalogCount === 1 ? 'Catalog' : 'Catalogs'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-primary-600">
                {schemaCount}
              </div>
              <div className="text-sm text-surface-500">
                {schemaCount === 1 ? 'Schema' : 'Schemas'}
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="ghost" onClick={onBack}>
            Back
          </Button>
          <Button onClick={onNext}>
            Continue
          </Button>
        </div>
      </div>
    );
  }

  // Failure state
  return (
    <div className="flex flex-col items-center text-center px-6 py-8">
      <ErrorIcon />

      <h2 className="text-xl font-semibold text-surface-900 mt-4 mb-2">
        Connection Failed
      </h2>

      <p className="text-surface-600 mb-2">
        We couldn't connect to your Databricks workspace.
      </p>

      {testResult?.error && (
        <div className="bg-error/10 text-error rounded-lg p-3 mb-6 max-w-md">
          <p className="text-sm">{testResult.error}</p>
        </div>
      )}

      <div className="bg-surface-50 rounded-lg p-4 mb-8 max-w-md text-left">
        <h3 className="text-sm font-medium text-surface-700 mb-2">
          Common issues:
        </h3>
        <ul className="text-sm text-surface-600 space-y-1">
          <li className="flex items-start gap-2">
            <span className="text-surface-400 mt-0.5">•</span>
            <span>Check that your workspace URL is correct</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-surface-400 mt-0.5">•</span>
            <span>Verify the SQL Warehouse is running</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-surface-400 mt-0.5">•</span>
            <span>Ensure your access token hasn't expired</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-surface-400 mt-0.5">•</span>
            <span>Confirm the token has read permissions</span>
          </li>
        </ul>
      </div>

      <div className="flex gap-3">
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="secondary" onClick={onBack}>
          Edit Credentials
        </Button>
        <Button onClick={onRetry}>
          Try Again
        </Button>
      </div>
    </div>
  );
}
